import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, CircularProgress } from "@material-ui/core";
import { faPlus } from "@fortawesome/pro-light-svg-icons";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import type { IModalService } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services";
import type { ITeamsRepository } from "App/Modules/Repositories";
import type { IResponseWithLoading, ITeamSlim } from "App/Modules/Models";
import { jsonEqual, SearchField } from "../../Shared";

import "./_teams.scss";

type InjectedProps = {
    mmTeamsRepository: ITeamsRepository;
    mmModalService: IModalService;
};

type TeamsProps = InjectedProps;

export const Teams = (props: TeamsProps) => {
    const [globalFilter, setGlobalFilter] = useState("");
    const [teams, setTeams] = useState<IResponseWithLoading<ITeamSlim[]>>();
    const [loadingTeams, setLoadingTeams] = useState(true);
    useEffect(() => {
        if (!teams) {
            extendWithLoadingAndFail(props.mmTeamsRepository.getTeams()).subscribe(
                (response) => {
                    if (!response.isLoading && !jsonEqual(response, teams)) {
                        setTeams(response);
                        setLoadingTeams(false);
                    }
                },
                (error) => {
                    setTeams(error);
                    setLoadingTeams(false);
                },
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const { t } = useTranslation();
    const filterFunc = (team: ITeamSlim) => {
        if (!globalFilter) {
            return true;
        }
        return team.name.toLowerCase().indexOf(globalFilter.toLowerCase()) > -1;
    };
    const addNewTeam = (event) => {
        event.preventDefault();
        props.mmModalService.open("mmAddTeam", null).subscribe((response: ITeamSlim) => {
            teams.data.push(response);
            window.location.hash = `#!/teams/${response.id}`;
        });
    };
    return (
        <div className="teams card">
            <h1 className="title title--h2">{t("Entities.Team.Teams")}</h1>
            <div className="teams__functions">
                <div className="teams__filter">
                    <SearchField globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
                </div>
                <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    startIcon={<FontAwesomeIcon icon={faPlus} fixedWidth />}
                    onClick={addNewTeam}
                >
                    {t("Gui.Buttons.AddEntity", {
                        entityName: t("Entities.Team.Team").toLowerCase(),
                    })}
                </Button>
            </div>
            <div className="scrollable-list card card--nested card--alt">
                <div className="scrollable-list__scroller scrollable-list__scroller--large">
                    <div className="entity-list">
                        {loadingTeams ? (
                            <div className="entity-list__item entity-list__item--loading">
                                <CircularProgress />
                            </div>
                        ) : null}
                        {teams?.data?.map((team) => {
                            if (filterFunc(team)) {
                                return (
                                    <div className="entity-list__item" key={team.id}>
                                        <a
                                            className="entity-list__item-link entity-list__item-link--padding"
                                            href={`/#!/teams/${team.id}`}
                                        >
                                            <div className="entity-list__item-panel">
                                                <div className="paragraph paragraph--no-spacing paragraph--bold">
                                                    {team.name}
                                                </div>
                                                <div className="caption caption--no-spacing">
                                                    {t("Entities.Team.NumSites", {
                                                        count: team.sitesCount,
                                                        postProcess: "interval",
                                                    })}
                                                </div>
                                            </div>
                                            <div className="entity-list__item-arrow">
                                                <FontAwesomeIcon icon={faChevronRight} />
                                            </div>
                                        </a>
                                    </div>
                                );
                            }
                            return null;
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
